import { useEffect, useState, useCallback, useRef } from "react";

// Real GPS hook using navigator.geolocation.watchPosition
// Reports {lat,lng,accuracy} and a status: 'idle' | 'requesting' | 'granted' | 'denied' | 'unsupported' | 'error'
export function useGeoTracking({ enabled, onUpdate, minIntervalMs = 4000 }) {
  const [coords, setCoords] = useState(null);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const watchRef = useRef(null);
  const lastSentRef = useRef(0);

  const start = useCallback(() => {
    if (!("geolocation" in navigator)) {
      setStatus("unsupported");
      setError("متصفحك لا يدعم تحديد الموقع");
      return;
    }
    setStatus("requesting");
    setError("");
    watchRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const c = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          heading: pos.coords.heading,
        };
        setCoords(c);
        setStatus("granted");
        const now = Date.now();
        if (onUpdate && now - lastSentRef.current >= minIntervalMs) {
          lastSentRef.current = now;
          onUpdate(c);
        }
      },
      (e) => {
        if (e.code === 1) { setStatus("denied"); setError("تم رفض إذن الموقع"); }
        else if (e.code === 2) { setStatus("error"); setError("تعذّر تحديد الموقع"); }
        else if (e.code === 3) { setStatus("error"); setError("انتهت مهلة تحديد الموقع"); }
        else { setStatus("error"); setError(e.message || "خطأ غير معروف"); }
      },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
    );
  }, [onUpdate, minIntervalMs]);

  const stop = useCallback(() => {
    if (watchRef.current !== null) {
      navigator.geolocation.clearWatch(watchRef.current);
      watchRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (enabled) start();
    else stop();
    return stop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return { coords, status, error, start, stop };
}

// High-precision one-shot: sample GPS multiple times (up to `maxSamples`) and return the reading
// with the SMALLEST accuracy value (best GPS lock). Falls back to first reading if none improve.
// Returns as soon as a reading with accuracy <= targetAccuracyM is received (default 15m).
export function getCurrentPositionOnce({
  timeout = 15000, targetAccuracyM = 15, maxSamples = 4, ...extra
} = {}) {
  return new Promise((resolve, reject) => {
    if (!("geolocation" in navigator)) {
      reject(new Error("متصفحك لا يدعم تحديد الموقع"));
      return;
    }
    let watchId = null;
    let best = null;
    let sampleCount = 0;
    const finish = () => {
      if (watchId !== null) navigator.geolocation.clearWatch(watchId);
      if (best) resolve(best); else reject(new Error("تعذّر تحديد الموقع"));
    };
    const timeoutId = setTimeout(finish, timeout);
    const opts = { enableHighAccuracy: true, maximumAge: 0, timeout, ...extra };
    watchId = navigator.geolocation.watchPosition(
      (p) => {
        sampleCount++;
        const c = { lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy };
        if (!best || c.accuracy < best.accuracy) best = c;
        // Return as soon as we have a good-enough lock, or after sampling enough times
        if (best.accuracy <= targetAccuracyM || sampleCount >= maxSamples) {
          clearTimeout(timeoutId);
          finish();
        }
      },
      (e) => {
        clearTimeout(timeoutId);
        if (watchId !== null) navigator.geolocation.clearWatch(watchId);
        if (e.code === 1) reject(new Error("تم رفض إذن الموقع. يرجى السماح بالوصول للموقع من إعدادات المتصفح."));
        else if (e.code === 2) reject(new Error("تعذّر تحديد الموقع — جرّب مجدداً في مكان مفتوح للحصول على إشارة GPS"));
        else if (e.code === 3) reject(new Error("انتهت مهلة تحديد الموقع"));
        else reject(new Error(e.message || "خطأ غير معروف"));
      },
      opts
    );
  });
}
